import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { SubItemCategory } from '@jobos/shared';
import { DEFAULT_SUB_ITEM_CATEGORIES, buildSubItemTypeMetaMap, type SubItemTypeMetaMap } from '@jobos/shared';
import { useSubItemCategories } from '@/hooks/useSubItemCategories';
import { useCarpetCatalog } from '@/hooks/useCarpetCatalog';
import { useEquipmentCatalog } from '@/hooks/useEquipmentCatalog';

interface SubItemCategoriesContextValue {
  categories: SubItemCategory[];
  typeMeta: SubItemTypeMetaMap;
  isLoading: boolean;
}

const DEFAULT_CATEGORIES = DEFAULT_SUB_ITEM_CATEGORIES.map((c, i) => ({ ...c, id: `default-${i}` }));

const SubItemCategoriesContext = createContext<SubItemCategoriesContextValue>({
  categories: DEFAULT_CATEGORIES,
  typeMeta: buildSubItemTypeMetaMap(DEFAULT_CATEGORIES, []),
  isLoading: false,
});

export function SubItemCategoriesProvider({ children }: { children: ReactNode }) {
  const { data: categoriesData, isLoading: categoriesLoading } = useSubItemCategories();
  // Include carpet + equipment catalogs in the type-metadata map so the calculator and
  // label helpers can resolve carpet_* / equipment_* keys (pricingMode, unitLabel,
  // label). Equipment + carpet catalog keys are intentionally absent from
  // sub_item_categories so the breakdown calculator's missing-category branch applies
  // (markup/tax not applicable — both are charge-through).
  const { data: carpetCatalogData } = useCarpetCatalog();
  const { data: equipmentCatalogData } = useEquipmentCatalog();

  const categories = categoriesData && categoriesData.length > 0
    ? categoriesData
    : DEFAULT_CATEGORIES;

  const typeMeta = useMemo(
    () => buildSubItemTypeMetaMap(categories, carpetCatalogData ?? [], equipmentCatalogData),
    [categories, carpetCatalogData, equipmentCatalogData],
  );

  return (
    <SubItemCategoriesContext.Provider value={{ categories, typeMeta, isLoading: categoriesLoading }}>
      {children}
    </SubItemCategoriesContext.Provider>
  );
}

export function useSubItemCategoriesContext() {
  return useContext(SubItemCategoriesContext);
}

export function useSubItemTypeMeta(): SubItemTypeMetaMap {
  const { typeMeta } = useSubItemCategoriesContext();
  return typeMeta;
}
